import usePagination from "~/hooks/usePagination";
import useCurrentUser from "~/hooks/user/useCurrentUser";
import useUrlParams from "~/hooks/useUrlParams";
import type { PaginationUrlParams } from "~/types";
import { supportedNotificationEvents } from "~/util/notificationFormatter";
import { whereToAnd } from "~/util/payload";

/**
 * Interface for the parameters of the useGetPaginatedNotifications function.
 * @interface
 * @property {boolean} markedRead - Whether to filter by read status.
 *
 */
interface Params {
  markedRead?: boolean;
}

/**
 * Hook to fetch paginated notification data.
 * It first fetches the current user's data to get the organization ID,
 * then fetches the notification data using that ID.
 * @param {Params} params - The parameters for the hook.
 * @returns {Object} The notification data and loading state.
 */
const useGetPaginatedNotifications = ({ markedRead }: Params) => {
  const {
    data: { internalRole },
    isLoading: userLoading,
  } = useCurrentUser();

  const { params } = useUrlParams();

  const thisParamSet = params?.["notifications"] as PaginationUrlParams;

  const where = {
    ...(thisParamSet?.where || {}),
  };

  // Filter by read status
  where.markedRead = {
    equalsExact: markedRead,
  };

  // Filter by tenant or organization
  if (internalRole === "tenant") {
    where.category = {
      equalsExact: "tenant",
    };
  } else {
    where.category = {
      equalsExact: "organization",
    };
  }

  // Only return supported events
  where.event = {
    in: supportedNotificationEvents.map((event) => event.event),
  };

  const { data: allNotificationsUnprocessed, ...rest } = usePagination({
    collection: "notifications",
    where: whereToAnd(where),
    sort: thisParamSet?.sort,
    page: thisParamSet?.page,
    limit: thisParamSet?.limit,
    depth: 5,
    serialLoading: userLoading,
    // Every 1 minute
    refreshInterval: 60000,
  });

  // Formatting happens here.
  const allNotifications = allNotificationsUnprocessed?.map((notification) => {
    return {
      ...notification,
    };
  });

  return {
    filterEnabled: thisParamSet?.where,
    data: allNotifications,
    ...rest,
  };
};

export default useGetPaginatedNotifications;
